/*!
 * Niagara UI Marketplace — Live Graphics Engine
 * Lightweight canvas renderer that draws small "live" BMS-style equipment
 * animations (AHU airflow, VAV dampers, RTU fans, chiller loops, floorplans,
 * gauges, dashboards, icon packs). Used for the hero visual, product-card
 * hover/tap previews, and the product page gallery.
 * No dependencies. Respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function cssVar(name, fallback) {
    var v = getComputedStyle(document.documentElement).getPropertyValue(name);
    return (v && v.trim()) || fallback;
  }

  // Structural line work (duct walls, equipment casings, housings, labels)
  // for the equipment graphics. These render inside .hmi panels, whose
  // background follows the site theme via --c-panel — so the ink has to flip
  // with it, or the line work vanishes into the surface. Cached rather than
  // recomputed per stroke, since this is called many times per frame.
  var INK = '255,255,255';
  function refreshInk() {
    INK = document.documentElement.getAttribute('data-theme') === 'light'
      ? '15,23,42'    // slate ink on a light panel
      : '255,255,255'; // light ink on a dark panel
  }
  refreshInk();
  document.addEventListener('niagara:theme-change', function () {
    refreshInk();
    // Repaint anything that is currently parked on a static frame.
    if (renderers) renderers.forEach(function (r) { if (!r.active) r.renderStaticFrame(); });
  });

  function outline(alpha) { return 'rgba(' + INK + ',' + alpha + ')'; }

  function colors() {
    return {
      cyan: cssVar('--c-cyan', '#29f1c8'),
      cyanSoft: 'rgba(41,241,200,0.14)',
      amber: cssVar('--c-amber', '#ffb020'),
      violet: cssVar('--c-violet', '#7c6cff'),
      mute: outline(0.18),
      grid: 'rgba(41,241,200,0.055)',
      text: cssVar('--c-text', '#f3f7f7'),
      danger: cssVar('--c-danger', '#ff5c5c'),
      // realistic BMS status-color convention (matches the color legend
      // used across the site): blue = cool/below setpoint, green = normal /
      // satisfied / running, amber-red = warm / alarm.
      cool: '#3b82f6',
      good: '#22c55e',
      warm: '#f97316',
      hot: '#ef4444'
    };
  }

  // Set while the renderer has already laid down a full-bleed grid, so the
  // per-equipment draw functions' own drawGrid() call becomes a no-op instead
  // of painting a second, differently-spaced grid inside the letterbox.
  var gridDrawn = false;

  function drawGrid(ctx, w, h) {
    if (gridDrawn) return;
    var c = colors();
    ctx.strokeStyle = c.grid;
    ctx.lineWidth = 1;
    var step = Math.max(20, Math.round(w / 14));
    for (var x = 0; x <= w; x += step) {
      ctx.beginPath(); ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, h); ctx.stroke();
    }
    for (var y = 0; y <= h; y += step) {
      ctx.beginPath(); ctx.moveTo(0, y + 0.5); ctx.lineTo(w, y + 0.5); ctx.stroke();
    }
  }

  function glowDot(ctx, x, y, r, color) {
    var grad = ctx.createRadialGradient(x, y, 0, x, y, r * 4);
    grad.addColorStop(0, color);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.arc(x, y, r * 4, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = color;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  }

  function clamp01(v) { return Math.max(0, Math.min(1, v)); }

  // Interpolates a blue -> green -> amber -> red thermal scale, 0..1.
  function thermalColor(v) {
    v = Math.max(0, Math.min(1, v));
    var stops = [
      [0.00, [59, 130, 246]],   // cool blue
      [0.42, [34, 197, 94]],    // satisfied green
      [0.70, [249, 168, 37]],   // warm amber
      [1.00, [239, 68, 68]]     // hot red
    ];
    for (var i = 0; i < stops.length - 1; i++) {
      var a = stops[i], b = stops[i + 1];
      if (v >= a[0] && v <= b[0]) {
        var p = (v - a[0]) / (b[0] - a[0]);
        var r = Math.round(a[1][0] + (b[1][0] - a[1][0]) * p);
        var g = Math.round(a[1][1] + (b[1][1] - a[1][1]) * p);
        var bl = Math.round(a[1][2] + (b[1][2] - a[1][2]) * p);
        return 'rgb(' + r + ',' + g + ',' + bl + ')';
      }
    }
    return 'rgb(239,68,68)';
  }

  /* ---------------------------------------------------------------- */
  /* Draw routines — each receives (ctx, w, h, t) where t is seconds   */
  /* ---------------------------------------------------------------- */

  var draw = {};

  draw.ahu = function (ctx, w, h, t, cfg) {
    var c = colors();
    drawGrid(ctx, w, h);

    // A cooling-season air handler: outside air mixed with return, pulled
    // through a filter, cooled to a 55°F supply setpoint, heating coil idle.
    var MIXED_T = 62, SUPPLY_T = 55;
    var y0 = h * 0.26, y1 = h * 0.74, cy = (y0 + y1) / 2;
    var x0 = w * 0.06, x1 = w * 0.94;
    var showText = w > 300;

    // casing
    ctx.strokeStyle = outline(0.24); ctx.lineWidth = 1.4;
    ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);

    // section boundaries across the unit, as fractions of the casing width
    function px(f) { return x0 + (x1 - x0) * f; }
    var SECTIONS = [
      { at: 0.16, label: 'OA DMPR' },
      { at: 0.32, label: 'FILTER' },
      { at: 0.50, label: 'CLG COIL' },
      { at: 0.66, label: 'HTG COIL' },
      { at: 0.84, label: 'SUPPLY FAN' }
    ];
    ctx.strokeStyle = outline(0.13); ctx.lineWidth = 1;
    SECTIONS.forEach(function (s) {
      ctx.beginPath(); ctx.moveTo(px(s.at), y0); ctx.lineTo(px(s.at), y1); ctx.stroke();
    });

    // ---- air stream, colored by the air's actual temperature ------------
    function chevrons(a, b, tempF, speed) {
      var xa = px(a), xb = px(b), span = xb - xa;
      if (span <= 6) return;
      var n = Math.max(1, Math.round(span / (w * 0.055)));
      var gap = span / n;
      ctx.strokeStyle = airColor(tempF); ctx.lineWidth = 2;
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      for (var i = 0; i < n; i++) {
        var x = xa + ((t * speed + i * gap) % span);
        var edge = Math.min(x - xa, xb - x) / (span * 0.28);
        ctx.globalAlpha = 0.75 * Math.max(0, Math.min(1, edge));
        ctx.beginPath();
        ctx.moveTo(x - 5, cy - 6); ctx.lineTo(x + 3, cy); ctx.lineTo(x - 5, cy + 6);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }
    chevrons(-0.06, 0.16, MIXED_T, 52);   // outside air entering
    chevrons(0.17, 0.40, MIXED_T, 52);    // mixed air through the filter
    chevrons(0.60, 0.82, SUPPLY_T, 60);   // cooled air off the coil
    chevrons(0.90, 1.10, SUPPLY_T, 66);   // supply air leaving

    // ---- outside-air damper: angled blades ------------------------------
    var dmpr = 0.32 + Math.sin(t * 0.5) * 0.08;   // slowly modulating
    var dx = px(0.08), bladeH = (y1 - y0) / 4;
    ctx.strokeStyle = outline(0.62); ctx.lineWidth = 2.4; ctx.lineCap = 'round';
    for (var b = 0; b < 4; b++) {
      var by = y0 + bladeH * (b + 0.5);
      ctx.save(); ctx.translate(dx, by);
      ctx.rotate((1 - dmpr) * (Math.PI / 2));
      ctx.beginPath(); ctx.moveTo(-bladeH * 0.42, 0); ctx.lineTo(bladeH * 0.42, 0); ctx.stroke();
      ctx.restore();
    }

    // ---- filter: hatched panel -------------------------------------------
    var fx0 = px(0.20), fx1 = px(0.29);
    ctx.strokeStyle = outline(0.30); ctx.lineWidth = 1;
    ctx.strokeRect(fx0, y0 + 4, fx1 - fx0, y1 - y0 - 8);
    ctx.beginPath();
    for (var fx = fx0; fx < fx1 + (y1 - y0); fx += 7) {
      ctx.moveTo(fx, y1 - 4); ctx.lineTo(fx - (y1 - y0 - 8), y0 + 4);
    }
    ctx.save();
    ctx.rect(fx0, y0 + 4, fx1 - fx0, y1 - y0 - 8); ctx.clip(); ctx.stroke();
    ctx.restore();

    // ---- cooling coil (active) -------------------------------------------
    function coil(cx0, cx1, rgb, active) {
      ctx.fillStyle = 'rgba(' + rgb + ',' + (active ? 0.26 : 0.05) + ')';
      ctx.fillRect(cx0, y0 + 4, cx1 - cx0, y1 - y0 - 8);
      ctx.strokeStyle = active ? 'rgb(' + rgb + ')' : outline(0.22);
      ctx.lineWidth = 1.3;
      for (var x = cx0 + 3; x < cx1; x += 4) {
        ctx.beginPath(); ctx.moveTo(x, y0 + 6); ctx.lineTo(x, y1 - 6); ctx.stroke();
      }
    }
    coil(px(0.36), px(0.47), '59,130,246', true);    // chilled water — running
    coil(px(0.53), px(0.63), '249,115,22', false);   // hot water — idle

    // ---- supply fan --------------------------------------------------------
    var fanX = px(0.75), fr = Math.min((x1 - x0) * 0.055, (y1 - y0) * 0.34);
    ctx.save();
    ctx.translate(fanX, cy);
    ctx.rotate(t * 3.4);
    ctx.strokeStyle = c.amber; ctx.lineWidth = 2.2; ctx.lineCap = 'round';
    for (var i = 0; i < 4; i++) {
      ctx.save(); ctx.rotate((Math.PI / 2) * i);
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.quadraticCurveTo(fr * 0.6, fr * 0.15, fr, 0);
      ctx.stroke(); ctx.restore();
    }
    ctx.restore();
    ctx.strokeStyle = c.good; ctx.lineWidth = 1.4;
    ctx.globalAlpha = 0.7 + Math.sin(t * 3) * 0.3;
    ctx.beginPath(); ctx.arc(fanX, cy, fr + 5, 0, Math.PI * 2); ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.fillStyle = c.amber;
    ctx.beginPath(); ctx.arc(fanX, cy, 3, 0, Math.PI * 2); ctx.fill();

    // ---- labels and readouts ------------------------------------------------
    if (showText) {
      ctx.font = '600 9px ui-monospace, SFMono-Regular, Menlo, monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = outline(0.42);
      var prev = 0;
      SECTIONS.concat([{ at: 1.0 }]).forEach(function (s, i) {
        var lbl = ['OA DMPR', 'FILTER', 'CLG COIL', 'HTG COIL', 'SUPPLY FAN', 'DISCHARGE'][i];
        ctx.fillText(lbl, px((prev + s.at) / 2), y1 + 15);
        prev = s.at;
      });

      ctx.font = '600 10px ui-monospace, SFMono-Regular, Menlo, monospace';
      ctx.textAlign = 'left';
      ctx.fillStyle = airColor(MIXED_T);
      ctx.fillText('MIXED ' + MIXED_T + '°F', x0, y0 - 9);
      ctx.textAlign = 'right';
      ctx.fillStyle = airColor(SUPPLY_T);
      ctx.fillText('SUPPLY ' + SUPPLY_T + '°F', x1, y0 - 9);
      ctx.textAlign = 'left';
    }
  };

  /* ------------------------------------------------------------------ *
   * VAV terminal-unit control sequence — dual maximum, with hot-water    *
   * reheat (the arrangement in ASHRAE Guideline 36).                     *
   *                                                                      *
   * ZONE TEMPERATURE is the input. Damper position, airflow, reheat      *
   * valve and discharge-air temperature are all DERIVED from it, exactly *
   * as they are on a real box: the damper opens BECAUSE the zone is warm *
   * and calling for cooling — not the other way around.                  *
   *                                                                      *
   * This is the single source of truth shared by the canvas renderer and *
   * the on-page readouts, so the picture and the numbers beside it can   *
   * never drift apart.                                                   *
   * ------------------------------------------------------------------ */
  var VAV = {
    HTG_SP: 70,         // °F  zone heating setpoint
    CLG_SP: 74,         // °F  zone cooling setpoint
    THROTTLE: 3,        // °F  throttling range on each loop
    FLOW_MIN: 300,      // CFM minimum / ventilation airflow
    FLOW_CLG_MAX: 1200, // CFM cooling maximum
    FLOW_HTG_MAX: 600,  // CFM heating maximum (dual max = 50% of cooling max)
    PRIMARY_T: 55,      // °F  primary "cold deck" air arriving from the AHU
    COIL_RISE: 40       // °F  design air-temperature rise across the coil
  };

  function vavSequence(zoneTemp) {
    var s = VAV, mode, flow, valve, coolLoop = 0, heatLoop = 0;

    if (zoneTemp > s.CLG_SP) {
      // Warm zone -> cooling loop drives the damper open for more cold
      // primary air. Reheat stays closed.
      coolLoop = clamp01((zoneTemp - s.CLG_SP) / s.THROTTLE);
      flow = s.FLOW_MIN + coolLoop * (s.FLOW_CLG_MAX - s.FLOW_MIN);
      valve = 0;
      mode = 'cooling';
    } else if (zoneTemp < s.HTG_SP) {
      // Cold zone -> DUAL MAXIMUM. Hold minimum airflow and modulate the
      // reheat valve first; only once the valve is wide open does airflow
      // rise toward the (deliberately lower) heating maximum. Throwing more
      // 55°F air at a cold room before you've turned the coil on would just
      // make it colder — which is exactly why the order matters.
      heatLoop = clamp01((s.HTG_SP - zoneTemp) / s.THROTTLE);
      if (heatLoop <= 0.5) {
        flow = s.FLOW_MIN;
        valve = heatLoop / 0.5;
      } else {
        valve = 1;
        flow = s.FLOW_MIN + ((heatLoop - 0.5) / 0.5) * (s.FLOW_HTG_MAX - s.FLOW_MIN);
      }
      mode = 'heating';
    } else {
      // Deadband -> minimum ventilation airflow, no reheat.
      flow = s.FLOW_MIN; valve = 0; mode = 'satisfied';
    }

    // Air leaving the reheat coil. Spreading the same coil output over more
    // air gives a smaller rise, so discharge temperature eases back a little
    // as the box ramps toward heating max.
    var dat = s.PRIMARY_T + valve * s.COIL_RISE * (0.8 + 0.2 * (s.FLOW_MIN / flow));

    return {
      mode: mode,
      zoneTemp: zoneTemp,
      flow: flow,
      flowPct: flow / s.FLOW_CLG_MAX,
      valve: valve,
      dat: dat,
      // A damper is not linear with flow — most of the gain comes early.
      damper: clamp01(Math.pow(flow / s.FLOW_CLG_MAX, 0.75)),
      coolLoop: coolLoop,
      heatLoop: heatLoop,
      sp: s
    };
  }

  // Air is colored by its ACTUAL temperature: 55°F primary air reads blue,
  // reheated 90°F+ discharge air reads orange/red.
  function airColor(tempF) { return thermalColor((tempF - 50) / 45); }

  draw.vav = function (ctx, w, h, t, cfg) {
    drawGrid(ctx, w, h);

    // Slider-driven on the homepage demo; otherwise drift slowly through the
    // whole sequence so a thumbnail shows heating, deadband and cooling in turn.
    var hasControl = cfg && cfg.control !== null && cfg.control !== undefined && !isNaN(cfg.control);
    var zoneTemp = hasControl ? cfg.control : 72 + Math.sin(t * 0.4) * 6.5;
    var s = vavSequence(zoneTemp);

    var cy = h * 0.5;
    var ductH = Math.max(16, Math.min(h * 0.30, 86));
    var y0 = cy - ductH / 2, y1 = cy + ductH / 2;
    var xIn = w * 0.03, xBox0 = w * 0.34, xBox1 = w * 0.78, xOut = w * 0.985;
    var boxTop = cy - ductH * 1.05, boxBot = cy + ductH * 1.05;
    var xDamper = w * 0.20;
    var showText = w > 300;

    // ---- chevron air stream -----------------------------------------
    function chevrons(x0, x1, color, alpha) {
      var span = x1 - x0;
      if (span <= 8) return;
      var density = Math.max(2, Math.round(span / (w * 0.075)));
      var speed = 34 + s.flowPct * 210;   // faster air at higher flow
      var gap = span / density;
      ctx.strokeStyle = color; ctx.lineWidth = 2;
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      for (var i = 0; i < density; i++) {
        var x = x0 + ((t * speed + i * gap) % span);
        var edge = Math.min(x - x0, x1 - x) / (span * 0.2);
        ctx.globalAlpha = alpha * Math.max(0, Math.min(1, edge));
        ctx.beginPath();
        ctx.moveTo(x - 5, cy - 5); ctx.lineTo(x + 2, cy); ctx.lineTo(x - 5, cy + 5);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    // ---- ductwork ----------------------------------------------------
    ctx.strokeStyle = outline(0.20); ctx.lineWidth = 1.4;
    ctx.beginPath();                                  // inlet duct
    ctx.moveTo(xIn, y0); ctx.lineTo(xBox0, y0);
    ctx.moveTo(xIn, y1); ctx.lineTo(xBox0, y1);
    ctx.moveTo(xBox1, y0); ctx.lineTo(xOut, y0);      // discharge duct
    ctx.moveTo(xBox1, y1); ctx.lineTo(xOut, y1);
    ctx.stroke();

    // primary air is always ~55°F off the AHU cold deck
    chevrons(xIn + 4, xDamper - 12, airColor(s.sp.PRIMARY_T), 0.30 + s.flowPct * 0.5);

    // ---- inlet damper -------------------------------------------------
    // Blade lies flat along the airflow when open, square across it when shut.
    var bladeLen = ductH * 0.92;
    ctx.save();
    ctx.translate(xDamper, cy);
    ctx.rotate((1 - s.damper) * (Math.PI / 2));
    ctx.strokeStyle = outline(0.78); ctx.lineWidth = 3.4; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(-bladeLen / 2, 0); ctx.lineTo(bladeLen / 2, 0); ctx.stroke();
    ctx.restore();
    ctx.fillStyle = outline(0.55);
    ctx.beginPath(); ctx.arc(xDamper, cy, 2.6, 0, Math.PI * 2); ctx.fill();

    // air between damper and coil is still primary-temperature air
    chevrons(xDamper + 12, xBox1 - w * 0.20, airColor(s.sp.PRIMARY_T), 0.22 + s.flowPct * 0.45);

    // ---- terminal-unit casing ------------------------------------------
    ctx.strokeStyle = outline(0.26); ctx.lineWidth = 1.4;
    ctx.strokeRect(xBox0, boxTop, xBox1 - xBox0, boxBot - boxTop);

    // flow sensor cross
    var xs = xBox0 + (xBox1 - xBox0) * 0.14;
    ctx.strokeStyle = outline(0.32); ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(xs, y0 + 2); ctx.lineTo(xs, y1 - 2);
    ctx.moveTo(xs - 4, cy); ctx.lineTo(xs + 4, cy);
    ctx.stroke();

    // ---- hot-water reheat coil ------------------------------------------
    var cx0 = xBox1 - (xBox1 - xBox0) * 0.34, cx1 = xBox1 - (xBox1 - xBox0) * 0.10;
    var hot = 'rgb(249,115,22)';
    if (s.valve > 0.005) {                            // energized coil glows
      var g = ctx.createLinearGradient(cx0, 0, cx1, 0);
      g.addColorStop(0, 'rgba(249,115,22,' + (0.05 + 0.16 * s.valve) + ')');
      g.addColorStop(1, 'rgba(239,68,68,' + (0.10 + 0.30 * s.valve) + ')');
      ctx.fillStyle = g;
      ctx.fillRect(cx0, y0, cx1 - cx0, y1 - y0);
    }
    ctx.strokeStyle = s.valve > 0.005
      ? 'rgba(249,115,22,' + (0.45 + 0.55 * s.valve) + ')'
      : outline(0.24);
    ctx.lineWidth = 1.8; ctx.lineJoin = 'round';
    ctx.beginPath();                                  // serpentine tube
    var passes = 4, pw = (cx1 - cx0) / passes;
    ctx.moveTo(cx0, y1 - 3);
    for (var p = 0; p < passes; p++) {
      var px = cx0 + pw * p;
      ctx.lineTo(px + pw * 0.5, p % 2 === 0 ? y0 + 3 : y1 - 3);
      ctx.lineTo(px + pw, p % 2 === 0 ? y0 + 3 : y1 - 3);
    }
    ctx.stroke();

    // ---- discharge air, colored by its real temperature -------------------
    chevrons(xBox1 + 4, xOut - 4, airColor(s.dat), 0.34 + s.flowPct * 0.5);

    // ---- on-canvas labels --------------------------------------------------
    if (showText) {
      ctx.font = '600 10px ui-monospace, SFMono-Regular, Menlo, monospace';
      ctx.textAlign = 'left';
      ctx.fillStyle = airColor(s.sp.PRIMARY_T);
      ctx.fillText('PRIMARY ' + s.sp.PRIMARY_T + '°F', xIn + 2, y0 - 9);

      ctx.textAlign = 'right';
      ctx.fillStyle = airColor(s.dat);
      ctx.fillText('DISCHARGE ' + Math.round(s.dat) + '°F', xOut - 2, y0 - 9);

      ctx.textAlign = 'center';
      ctx.fillStyle = outline(0.62);
      ctx.fillText(Math.round(s.damper * 100) + '%', xDamper, y1 + 16);
      ctx.fillStyle = s.valve > 0.005 ? hot : outline(0.35);
      ctx.fillText('RH ' + Math.round(s.valve * 100) + '%', (cx0 + cx1) / 2, boxBot + 15);
      ctx.textAlign = 'left';
    }
  };

  draw.rtu = function (ctx, w, h, t) {
    var c = colors();
    drawGrid(ctx, w, h);
    ctx.strokeStyle = outline(0.22); ctx.lineWidth = 1.4;
    ctx.strokeRect(w * 0.14, h * 0.18, w * 0.72, h * 0.64);

    var f1x = w * 0.34, f2x = w * 0.66, fy = h * 0.5, fr = Math.min(w, h) * 0.1;
    [{ x: f1x, dir: 1, color: c.cyan }, { x: f2x, dir: -1, color: c.violet }].forEach(function (fan) {
      ctx.save();
      ctx.translate(fan.x, fy);
      ctx.rotate(t * 2.6 * fan.dir);
      ctx.strokeStyle = fan.color; ctx.lineWidth = 2; ctx.lineCap = 'round';
      for (var b = 0; b < 3; b++) {
        ctx.save(); ctx.rotate((Math.PI * 2 / 3) * b);
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(fr * 0.85, 0); ctx.stroke();
        ctx.restore();
      }
      ctx.restore();
      ctx.strokeStyle = outline(0.25); ctx.beginPath(); ctx.arc(fan.x, fy, fr, 0, Math.PI * 2); ctx.stroke();
    });

    // blinking status LEDs
    var leds = [c.cyan, c.amber, c.violet];
    for (var i = 0; i < 3; i++) {
      var on = Math.sin(t * 3 + i * 2) > 0;
      ctx.fillStyle = on ? leds[i] : outline(0.12);
      ctx.beginPath(); ctx.arc(w * 0.2 + i * 14, h * 0.24, 3, 0, Math.PI * 2); ctx.fill();
    }
  };

  draw.chiller = function (ctx, w, h, t) {
    var c = colors();
    drawGrid(ctx, w, h);
    var pad = 26;
    var path = [
      [w * 0.22, h * 0.28], [w * 0.78, h * 0.28],
      [w * 0.78, h * 0.72], [w * 0.22, h * 0.72], [w * 0.22, h * 0.28]
    ];
    ctx.strokeStyle = outline(0.22); ctx.lineWidth = 1.6; ctx.lineJoin = 'round';
    ctx.beginPath();
    path.forEach(function (p, i) { i === 0 ? ctx.moveTo(p[0], p[1]) : ctx.lineTo(p[0], p[1]); });
    ctx.stroke();

    // condenser zigzag (top)
    ctx.strokeStyle = c.amber; ctx.lineWidth = 1.6;
    ctx.beginPath();
    var zx0 = w * 0.3, zx1 = w * 0.7, zy = h * 0.28, seg = 8, zh = 6, dir = 1;
    ctx.moveTo(zx0, zy);
    for (var x = zx0; x <= zx1; x += seg) { ctx.lineTo(x, zy + zh * dir); dir *= -1; }
    ctx.stroke();

    // evaporator zigzag (bottom)
    ctx.strokeStyle = c.violet;
    ctx.beginPath();
    zy = h * 0.72; dir = 1; ctx.moveTo(zx0, zy);
    for (x = zx0; x <= zx1; x += seg) { ctx.lineTo(x, zy + zh * dir); dir *= -1; }
    ctx.stroke();

    // compressor box
    ctx.fillStyle = outline(0.05);
    ctx.strokeStyle = outline(0.3);
    ctx.fillRect(w * 0.44, h * 0.44, w * 0.12, h * 0.12);
    ctx.strokeRect(w * 0.44, h * 0.44, w * 0.12, h * 0.12);

    // animated refrigerant flow around the loop
    var total = 0, segs = [];
    for (var i = 0; i < path.length - 1; i++) {
      var a = path[i], b = path[i + 1];
      var len = Math.hypot(b[0] - a[0], b[1] - a[1]);
      segs.push({ a: a, b: b, len: len, start: total }); total += len;
    }
    var dotCount = 5;
    for (var d = 0; d < dotCount; d++) {
      var dist = ((t * 70) + (total / dotCount) * d) % total;
      for (var s = 0; s < segs.length; s++) {
        var seg2 = segs[s];
        if (dist >= seg2.start && dist <= seg2.start + seg2.len) {
          var pct = (dist - seg2.start) / seg2.len;
          var px = seg2.a[0] + (seg2.b[0] - seg2.a[0]) * pct;
          var py = seg2.a[1] + (seg2.b[1] - seg2.a[1]) * pct;
          glowDot(ctx, px, py, 2.4, c.cyan);
          break;
        }
      }
    }
  };

  draw.floorplan = function (ctx, w, h, t) {
    drawGrid(ctx, w, h);
    var x0 = w * 0.08, y0 = h * 0.12, x1 = w * 0.92, y1 = h * 0.88;
    var cols = 4, rows = 2;
    var cw = (x1 - x0) / cols, ch = (y1 - y0) / rows;

    for (var r = 0; r < rows; r++) {
      for (var col = 0; col < cols; col++) {
        var idx = r * cols + col;
        var rx = x0 + col * cw, ry = y0 + r * ch;
        // each room drifts slowly on its own phase so the whole map feels
        // "live" rather than a static heatmap image
        var v = 0.5 + Math.sin(t * 0.35 + idx * 1.7) * 0.42;
        var fill = thermalColor(v);
        ctx.fillStyle = fill; ctx.globalAlpha = 0.55;
        ctx.fillRect(rx + 1, ry + 1, cw - 2, ch - 2);
        ctx.globalAlpha = 1;
        ctx.strokeStyle = outline(0.28); ctx.lineWidth = 1;
        ctx.strokeRect(rx + 1, ry + 1, cw - 2, ch - 2);

        var temp = Math.round(66 + v * 20);
        ctx.fillStyle = 'rgba(6,8,10,0.55)';
        var label = temp + '°';
        ctx.font = '600 ' + Math.max(9, Math.round(cw * 0.13)) + 'px "JetBrains Mono", monospace';
        var tw = ctx.measureText(label).width;
        ctx.fillRect(rx + cw / 2 - tw / 2 - 4, ry + ch / 2 - 9, tw + 8, 16);
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(label, rx + cw / 2, ry + ch / 2 + 1);
      }
    }
    ctx.textAlign = 'start'; ctx.textBaseline = 'alphabetic';

    ctx.strokeStyle = outline(0.35); ctx.lineWidth = 1.6;
    ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
  };

  draw.gauge = function (ctx, w, h, t) {
    var c = colors();
    var cx = w / 2, cy = h * 0.58, r = Math.min(w, h) * 0.34;
    var start = Math.PI * 0.75, end = Math.PI * 2.25;

    ctx.strokeStyle = outline(0.14); ctx.lineWidth = 8; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.arc(cx, cy, r, start, end); ctx.stroke();

    var val = (Math.sin(t * 0.7) + 1) / 2; // 0..1
    var grad = ctx.createLinearGradient(cx - r, cy, cx + r, cy);
    grad.addColorStop(0, c.cyan); grad.addColorStop(1, c.violet);
    ctx.strokeStyle = grad; ctx.lineWidth = 8; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.arc(cx, cy, r, start, start + (end - start) * val); ctx.stroke();

    // ticks
    ctx.strokeStyle = outline(0.3); ctx.lineWidth = 1.5;
    for (var i = 0; i <= 10; i++) {
      var a = start + (end - start) * (i / 10);
      var x1 = cx + Math.cos(a) * (r - 12), y1 = cy + Math.sin(a) * (r - 12);
      var x2 = cx + Math.cos(a) * (r - 4), y2 = cy + Math.sin(a) * (r - 4);
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    }

    // needle
    var na = start + (end - start) * val;
    ctx.strokeStyle = c.amber; ctx.lineWidth = 2.4; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx + Math.cos(na) * (r - 18), cy + Math.sin(na) * (r - 18)); ctx.stroke();
    ctx.fillStyle = c.amber; ctx.beginPath(); ctx.arc(cx, cy, 4, 0, Math.PI * 2); ctx.fill();
  };

  draw.dashboard = function (ctx, w, h, t) {
    var c = colors();
    drawGrid(ctx, w, h);
    // bar chart, left third
    var bx = w * 0.08, by = h * 0.72, bw = (w * 0.34) / 5;
    ctx.fillStyle = c.cyan;
    for (var i = 0; i < 5; i++) {
      var bh = (Math.sin(t * 1.3 + i) + 1.2) / 2.2 * (h * 0.42);
      ctx.globalAlpha = 0.55 + i * 0.08;
      ctx.fillRect(bx + i * (bw + 4), by - bh, bw, bh);
    }
    ctx.globalAlpha = 1;

    // sparkline, right two-thirds top
    ctx.strokeStyle = c.violet; ctx.lineWidth = 2; ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.beginPath();
    var sx = w * 0.48, sw = w * 0.44, sy = h * 0.32;
    for (var px = 0; px <= sw; px += 4) {
      var yy = sy + Math.sin((px * 0.06) + t * 2) * 12 + Math.sin(px * 0.02 + t) * 6;
      px === 0 ? ctx.moveTo(sx + px, yy) : ctx.lineTo(sx + px, yy);
    }
    ctx.stroke();

    // small dial bottom right
    var gx = w * 0.75, gy = h * 0.72, gr = Math.min(w, h) * 0.12;
    var v = (Math.sin(t * 0.9) + 1) / 2;
    ctx.strokeStyle = outline(0.15); ctx.lineWidth = 5;
    ctx.beginPath(); ctx.arc(gx, gy, gr, Math.PI * 0.75, Math.PI * 2.25); ctx.stroke();
    ctx.strokeStyle = c.amber; ctx.beginPath(); ctx.arc(gx, gy, gr, Math.PI * 0.75, Math.PI * 0.75 + Math.PI * 1.5 * v); ctx.stroke();
  };

  draw['icon-pack'] = function (ctx, w, h, t) {
    drawGrid(ctx, w, h);
    var c = colors();
    var cols = 4, rows = 3, pad = w * 0.12;
    var cw = (w - pad * 2) / cols, ch = (h - pad * 2) / rows;
    var glyphs = [draw.ahu, draw.vav, draw.gauge];
    for (var r = 0; r < rows; r++) {
      for (var col = 0; col < cols; col++) {
        var idx = r * cols + col;
        var cx = pad + cw * col + cw / 2, cy = pad + ch * r + ch / 2;
        var phase = (t * 0.6 + idx * 0.35) % 3;
        var alpha = phase < 1.5 ? phase / 1.5 : 1 - (phase - 1.5) / 1.5;
        ctx.globalAlpha = Math.max(0.12, alpha);
        ctx.strokeStyle = idx % 3 === 0 ? c.cyan : (idx % 3 === 1 ? c.amber : c.violet);
        ctx.lineWidth = 1.6;
        ctx.strokeRect(cx - cw * 0.24, cy - ch * 0.24, cw * 0.48, ch * 0.48);
        ctx.beginPath(); ctx.arc(cx, cy, Math.min(cw, ch) * 0.12, 0, Math.PI * 2); ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  };

  /* ---------------------------------------------------------------- */
  /* Dashboard-showcase widgets — small reusable "chart" renderers used */
  /* by the animated dashboard-preview section (KPI sparklines, donut,  */
  /* area chart). These read their config from cfg (parsed from the     */
  /* canvas's data attributes once, in Renderer).                       */
  /* ---------------------------------------------------------------- */

  draw.sparkline = function (ctx, w, h, t, cfg) {
    var color = (cfg && cfg.color) || colors().cyan;
    var rising = !cfg || cfg.trend !== 'down';
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    var pts = [];
    var n = 24;
    for (var i = 0; i <= n; i++) {
      var px = (i / n) * w;
      var drift = rising ? (i / n) * (h * 0.28) : -(i / n) * (h * 0.28);
      var yy = h * 0.62 - drift + Math.sin(i * 0.9 + t * 1.6) * (h * 0.08) + Math.sin(i * 0.35 - t * 0.8) * (h * 0.06);
      pts.push([px, Math.max(h * 0.14, Math.min(h * 0.86, yy))]);
    }
    // gradient area fill
    var grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, color.indexOf('#') === 0 ? hexToRgba(color, 0.35) : color);
    grad.addColorStop(1, hexToRgba(color, 0));
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    pts.forEach(function (p) { ctx.lineTo(p[0], p[1]); });
    ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath();
    ctx.fillStyle = grad; ctx.fill();

    ctx.beginPath();
    pts.forEach(function (p, i) { i === 0 ? ctx.moveTo(p[0], p[1]) : ctx.lineTo(p[0], p[1]); });
    ctx.strokeStyle = color; ctx.stroke();

    // leading dot
    var last = pts[pts.length - 1];
    glowDot(ctx, last[0], last[1], 2.4, color);
  };

  function hexToRgba(hex, a) {
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(function (c) { return c + c; }).join('');
    var r = parseInt(hex.substring(0, 2), 16), g = parseInt(hex.substring(2, 4), 16), b = parseInt(hex.substring(4, 6), 16);
    return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
  }

  draw.donut = function (ctx, w, h, t, cfg) {
    var segments = (cfg && cfg.segments) || [
      { v: 75, c: colors().good },
      { v: 14, c: colors().cool },
      { v: 7, c: colors().warm },
      // this donut renders on a permanently light card, so "offline" is a
      // fixed neutral grey rather than theme ink
      { v: 4, c: '#cbd5e1' }
    ];
    var cx = w / 2, cy = h / 2, r = Math.min(w, h) * 0.42, thickness = r * 0.34;
    var total = segments.reduce(function (s, seg) { return s + seg.v; }, 0) || 1;
    var start = -Math.PI / 2;

    ctx.lineCap = 'butt';
    segments.forEach(function (seg, i) {
      // the active segment "breathes" a little so the chart reads as live
      var breathe = i === 0 ? 1 + Math.sin(t * 1.6) * 0.02 : 1;
      var frac = (seg.v / total) * breathe;
      var end = start + frac * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(cx, cy, r, start, end);
      ctx.strokeStyle = seg.c; ctx.lineWidth = thickness;
      ctx.stroke();
      start = start + (seg.v / total) * Math.PI * 2;
    });

    // slow rotating highlight sweep for extra life
    var sweepA = (t * 0.6) % (Math.PI * 2);
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, sweepA, sweepA + 0.35);
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = thickness;
    ctx.globalCompositeOperation = 'overlay';
    ctx.stroke();
    ctx.restore();
  };

  draw.areachart = function (ctx, w, h, t, cfg) {
    var c = colors();
    var color = (cfg && cfg.color) || c.cyan;
    drawGrid(ctx, w, h);

    var n = 40, pts = [];
    for (var i = 0; i <= n; i++) {
      var px = (i / n) * w;
      var yy = h * 0.5
        + Math.sin(i * 0.35 - t * 1.1) * (h * 0.16)
        + Math.sin(i * 0.12 + t * 0.5) * (h * 0.12)
        + Math.sin(i * 0.9 - t * 2.2) * (h * 0.03);
      pts.push([px, yy]);
    }
    var grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, hexToRgba(color, 0.32));
    grad.addColorStop(1, hexToRgba(color, 0));
    ctx.beginPath();
    ctx.moveTo(0, h);
    pts.forEach(function (p) { ctx.lineTo(p[0], p[1]); });
    ctx.lineTo(w, h); ctx.closePath();
    ctx.fillStyle = grad; ctx.fill();

    ctx.beginPath();
    pts.forEach(function (p, i) { i === 0 ? ctx.moveTo(p[0], p[1]) : ctx.lineTo(p[0], p[1]); });
    ctx.strokeStyle = color; ctx.lineWidth = 2.2; ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.stroke();

    // secondary faint line (e.g. "average") for depth
    ctx.beginPath();
    pts.forEach(function (p, i) {
      var yy = p[1] + Math.sin(i * 0.5 + t) * 4 + h * 0.12;
      i === 0 ? ctx.moveTo(p[0], yy) : ctx.lineTo(p[0], yy);
    });
    ctx.strokeStyle = hexToRgba(c.violet, 0.55); ctx.lineWidth = 1.6;
    ctx.stroke();
  };

  draw.bars = function (ctx, w, h, t, cfg) {
    var color = (cfg && cfg.color) || colors().cyan;
    var n = 7, gap = w * 0.02, bw = (w - gap * (n - 1)) / n;
    for (var i = 0; i < n; i++) {
      var bh = (0.35 + (Math.sin(t * 1.1 + i * 0.9) + 1) / 2 * 0.6) * h * 0.82;
      var x = i * (bw + gap);
      var grad = ctx.createLinearGradient(0, h - bh, 0, h);
      grad.addColorStop(0, color);
      grad.addColorStop(1, hexToRgba(color, 0.25));
      ctx.fillStyle = grad;
      ctx.fillRect(x, h - bh, bw, bh);
    }
  };

  draw.offline = function (ctx, w, h, t) {
    var c = colors();
    drawGrid(ctx, w, h);
    ctx.strokeStyle = c.danger; ctx.lineWidth = 2; ctx.lineCap = 'round';
    ctx.beginPath();
    var cy = h / 2;
    for (var x = 0; x <= w; x += 3) {
      var glitch = (Math.sin(x * 0.4 + t * 40) > 0.985) ? (Math.random() - 0.5) * h * 0.5 : 0;
      var y = cy + Math.sin(x * 0.02 + t) * 3 + glitch;
      x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
    if (Math.sin(t * 6) > 0.7) {
      ctx.fillStyle = 'rgba(255,92,92,0.06)';
      ctx.fillRect(0, 0, w, h);
    }
  };

  /* ---------------------------------------------------------------- */
  /* Renderer wiring                                                   */
  /* ---------------------------------------------------------------- */

  function Renderer(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.type = canvas.getAttribute('data-graphic-type') || 'dashboard';
    this.mode = canvas.getAttribute('data-live-graphic');
    this.raf = null;
    this.active = false;
    this.t0 = performance.now();
    this.cfg = this.parseConfig();
    this.resize();
    this.renderStaticFrame();
  }

  Renderer.prototype.parseConfig = function () {
    var cfg = { control: null, color: this.canvas.getAttribute('data-color') || null, trend: this.canvas.getAttribute('data-trend') || null, segments: null };
    var raw = this.canvas.getAttribute('data-segments');
    if (raw) {
      try { cfg.segments = JSON.parse(raw); } catch (e) { cfg.segments = null; }
    }
    return cfg;
  };

  Renderer.prototype.resize = function () {
    var rect = this.canvas.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.w = Math.max(1, Math.round(rect.width));
    this.h = Math.max(1, Math.round(rect.height));
    this.canvas.width = this.w * dpr;
    this.canvas.height = this.h * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  // Equipment schematics are drawn in elevation and have a natural landscape
  // proportion. On a canvas wider than that, stretching them to fill leaves a
  // sparse, strung-out drawing — so cap the drawing width and center it, while
  // the background grid still runs the full width of the panel.
  var NATURAL_AR = { ahu: 1.85, vav: 1.85, rtu: 1.7, chiller: 1.7 };

  Renderer.prototype.paint = function (t) {
    var fn = draw[this.type] || draw.dashboard;
    var ctx = this.ctx, w = this.w, h = this.h;
    ctx.clearRect(0, 0, w, h);

    var ar = NATURAL_AR[this.type];
    var letterbox = ar && (w / h) > ar;

    if (letterbox) {
      gridDrawn = false;
      drawGrid(ctx, w, h);   // full-bleed grid first
      gridDrawn = true;      // suppress the draw fn's own grid
    }

    ctx.save();
    var dw = w;
    if (letterbox) { dw = h * ar; ctx.translate((w - dw) / 2, 0); }
    try { fn(ctx, dw, h, t, this.cfg); } catch (e) {}
    ctx.restore();

    gridDrawn = false;
  };

  Renderer.prototype.renderStaticFrame = function () {
    // Pick up the current control value so the very first paint already
    // matches the slider, rather than snapping once animation starts.
    var raw = this.canvas.getAttribute('data-control-value');
    this.cfg.control = raw !== null ? parseFloat(raw) : null;
    this.paint(0);
  };

  Renderer.prototype.start = function () {
    if (this.active || REDUCED_MOTION) return;
    this.active = true;
    var self = this;
    function frame(ts) {
      if (!self.active) return;
      var t = (ts - self.t0) / 1000;
      var rawControl = self.canvas.getAttribute('data-control-value');
      self.cfg.control = rawControl !== null ? parseFloat(rawControl) : null;
      self.paint(t);
      self.raf = requestAnimationFrame(frame);
    }
    this.raf = requestAnimationFrame(frame);
  };

  Renderer.prototype.stop = function () {
    this.active = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  };

  var renderers = [];

  function initAutoplay(renderer) {
    // hero / product gallery canvases: play while in viewport
    if (!('IntersectionObserver' in window)) { renderer.start(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        entry.isIntersecting ? renderer.start() : renderer.stop();
      });
    }, { threshold: 0.15 });
    io.observe(renderer.canvas);
  }

  function initCardHover(renderer) {
    var card = renderer.canvas.closest('.product-card');
    if (!card) return;

    card.addEventListener('mouseenter', function () { renderer.start(); });
    card.addEventListener('mouseleave', function () { renderer.stop(); });

    // .media is a plain div (not a link) so we can control tap-to-preview vs.
    // navigate ourselves: first tap on touch previews, second tap navigates;
    // a real mouse click navigates straight away.
    var media = renderer.canvas.closest('.media');
    if (media) {
      media.addEventListener('touchstart', function (e) {
        if (!card.classList.contains('is-touch-live')) {
          e.preventDefault();
          document.querySelectorAll('.product-card.is-touch-live').forEach(function (el) {
            el.classList.remove('is-touch-live');
          });
          card.classList.add('is-touch-live');
          renderer.start();
        }
      }, { passive: false });

      media.addEventListener('click', function () {
        var href = media.getAttribute('data-href');
        if (href) window.location.href = href;
      });
    }
  }

  function initProductToggle(renderer) {
    var wrap = renderer.canvas.closest('.gallery-main');
    var btn = document.querySelector('[data-live-toggle]');
    if (!wrap || !btn) return;
    btn.addEventListener('click', function () {
      var willBeLive = !wrap.classList.contains('is-live');
      wrap.classList.toggle('is-live', willBeLive);
      btn.classList.toggle('is-active', willBeLive);
      willBeLive ? renderer.start() : renderer.stop();
    });
  }

  function init() {
    var canvases = document.querySelectorAll('[data-live-graphic]');
    canvases.forEach(function (canvas) {
      var renderer = new Renderer(canvas);
      renderers.push(renderer);
      var mode = canvas.getAttribute('data-live-graphic');
      if (mode === 'hero' || mode === 'showcase' || mode === 'interactive') {
        // hero, dashboard-showcase widgets, and the playground: always play
        // while in view (playground also responds live to its slider).
        initAutoplay(renderer);
      } else if (mode === 'product') {
        // starts paused; toggled by the "Live preview" button, but autoplay once visible too
        initProductToggle(renderer);
        initAutoplay(renderer);
      } else if (mode === 'card') {
        initCardHover(renderer);
      }
    });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        renderers.forEach(function (r) { r.resize(); if (!r.active) r.renderStaticFrame(); });
      }, 150);
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) renderers.forEach(function (r) { r.wasActive = r.active; r.stop(); });
      else renderers.forEach(function (r) { if (r.wasActive) r.start(); });
    });
  }

  // Shared with theme.js so the interactive demo's readouts are computed from
  // the exact same sequence the canvas draws — never a parallel copy.
  window.NiagaraGraphics = {
    vavSequence: vavSequence,
    thermalColor: thermalColor,
    airColor: airColor,
    VAV: VAV
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.NiagaraLive = { Renderer: Renderer, draw: draw };
})();
